import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { CheckCircleFilled, Close } from "@payping/icons";
import { bnplMerchantGetContract } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Drawer,
  Flex,
  Spin,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
// This is imported here to only be downloaded in contract page not for the whole application
// eslint-disable-next-line import/no-unresolved
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

interface Props {
  open: boolean;
  onClose: () => void;
}
const BnplActivationContractDrawer: FC<Props> = ({ onClose, open }) => {
  const { t } = useAppTranslation();
  const divRef = useRef<HTMLDivElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const contract = useAppSelector((state) => state.bnplMerchant.contract);
  const dispatch = useAppDispatch();
  const [fileUrl, setFileUrl] = useState("");
  const [pdfError, setPdfError] = useState<string | null>(null);

  const [contractState, getContract] = useMessagedAsyncFn(
    () => dispatch(bnplMerchantGetContract()),
    [dispatch],
    { loading: false },
    { error: true, success: false },
  );

  const [pdfPageNumber, setPdfPageNumber] = useState<number | null>(null);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setPdfPageNumber(numPages);
    setPdfError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error("[BnplContract] PDF load error:", error);
    setPdfError(error.message);
  }, []);

  // Revoke previous blob URL to prevent memory leaks
  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => revokeObjectUrl();
  }, [revokeObjectUrl]);

  const [downloadState, downloadContract] = useMessagedAsyncFn(
    async () => {
      if (!contract?.rawContractFileName) return;

      const res = await bnplMerchantApis.getDownloadPath(contract.rawContractFileName);

      if (!res.url) {
        console.error("[BnplContract] No download URL received from API");
        setPdfError("No download URL available");
        return;
      }

      const response = await fetch(res.url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get("content-type") ?? "";
      if (!contentType.includes("application/pdf") && !contentType.includes("octet-stream")) {
        console.warn(`[BnplContract] Unexpected content type: ${contentType}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      if (arrayBuffer.byteLength === 0) {
        throw new Error("Downloaded file is empty");
      }

      revokeObjectUrl();
      const url = URL.createObjectURL(new Blob([arrayBuffer], { type: "application/pdf" }));
      objectUrlRef.current = url;
      setFileUrl(url);
      setPdfError(null);
    },
    [contract, revokeObjectUrl],
    { loading: true },
    { success: false, error: true },
  );

  // Auto-download contract when it becomes available
  useEffect(() => {
    if (open && contract?.rawContractFileName) {
      downloadContract();
    }
  }, [open, contract, downloadContract]);

  const [signContractState, signContract] = useMessagedAsyncFn(
    (contractId: number) => bnplMerchantApis.merchantSignContract({ contractId }),
    [],
  );

  const [agree, setAgree] = useState(false);

  const bodyHeight = document.body.getBoundingClientRect().height;
  const drawerHeaderHeight = 65;
  const totalPaddings = 64;
  const OtherContent = 24 + 32;
  const totalGaps = 32 + 32;

  const handleFinish = async () => {
    const contractId = contract?.contractId;
    if (contractId) {
      await signContract(contractId);
      await getContract();
    }
  };

  const handleCloseDrawer = () => {
    if (signContractState.value) {
      getContract();
    }
    onClose();
  };

  // eslint-disable-next-line react-hooks/refs
  const rect = divRef.current?.getBoundingClientRect();

  if (!open) return null;
  return (
    <Drawer
      closeIcon={false}
      size={"100%"}
      placement="bottom"
      open={open}
      onClose={handleCloseDrawer}
      title={t("shop.bnpl.activation.contract.drawer.title")}
      extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleCloseDrawer} />}
      classNames={{ body: "tw:p-0!" }}
    >
      {signContractState.value ? (
        <Flex vertical className="tw:mt-24! tw:w-full tw:p-8!" gap={"large"} align="center">
          <CheckCircleFilled width={80} className="tw:fill-color-success" />
          <Typography.Title level={5} className="tw:m-0!">
            {t("shop.bnpl.activation.contract.drawer.success")}
          </Typography.Title>
        </Flex>
      ) : (
        <Flex vertical className="tw:h-full tw:w-full tw:p-8!" gap={"32px"}>
          <Typography.Title level={5} className="tw:m-0!">
            {t("shop.bnpl.activation.contract.drawer.title2")}
          </Typography.Title>
          <div
            ref={divRef}
            className="tw:h-full tw:w-full tw:overflow-auto"
            style={{
              height:
                // 5 is for be sure
                bodyHeight - (drawerHeaderHeight + totalPaddings + OtherContent + totalGaps + 5),
            }}
          >
            {fileUrl && !pdfError ? (
              <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <Flex justify="center" align="center" className="tw:h-full">
                    <Spin />
                  </Flex>
                }
                className={"tw:w-full"}
              >
                {pdfPageNumber
                  ? Array.from(new Array(pdfPageNumber), (_, index) => (
                      <div key={`page_${index + 1}`}>
                        <Page
                          pageNumber={index + 1}
                          renderTextLayer={false}
                          width={(rect?.width ?? 0) - (lg ? 100 : 0)}
                        />
                      </div>
                    ))
                  : null}
              </Document>
            ) : pdfError ? (
              <Flex vertical justify="center" align="center" gap={"middle"} className="tw:h-full">
                <Typography.Text type="danger">{t("shop.bnpl.activation.contract.drawer.loadError")}</Typography.Text>
                <Button size="small" onClick={() => downloadContract()}>
                  {t("common.retry")}
                </Button>
              </Flex>
            ) : downloadState.loading ? (
              <Flex justify="center" align="center" className="tw:h-full">
                <Spin />
              </Flex>
            ) : null}
          </div>
          <Flex>
            <Checkbox checked={agree} onChange={(e: any) => setAgree(e.target.checked)}>
              {t("shop.bnpl.activation.contract.drawer.signContractCheck")}
            </Checkbox>
          </Flex>
          <Flex gap={"middle"}>
            <Button
              disabled={!agree}
              loading={signContractState.loading || contractState.loading}
              onClick={handleFinish}
              type="primary"
            >
              {t("shop.bnpl.activation.contract.drawer.confirm")}
            </Button>
          </Flex>
        </Flex>
      )}
    </Drawer>
  );
};

export default BnplActivationContractDrawer;
